import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, CalendarStar, UploadSimple, DownloadSimple, Trash, MapPin } from "@phosphor-icons/react";

export default function Holidays() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: "", name: "", branch: "" });
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);
  const [branchFilter, setBranchFilter] = useState("all");
  const [myBranch, setMyBranch] = useState(null);
  const fileRef = useRef(null);
  const canManage = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const tenantParams = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};

  const load = () => {
    api.get("/holidays", { params: tenantParams }).then(({ data }) => setRows(data));
    if (!canManage) api.get("/holidays/my-branch").then(({ data }) => setMyBranch(data.branch)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submit = async () => {
    setErr("");
    try {
      await api.post("/holidays", { ...form, branch: form.branch || null, ...tenantParams });
      toast.success("Holiday saved");
      setOpen(false); setForm({ date: "", name: "", branch: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/holidays/template");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([data.content], { type: "text/csv" })); a.download = data.filename; a.click();
  };
  const importFile = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/holidays/bulk-upload", fd, { params: tenantParams, headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`Saved ${data.saved} holiday(s) · ${data.errors.length} row(s) skipped: ${data.errors[0]}`);
      else toast.success(`Saved ${data.saved} holiday(s) from ${data.total_rows} rows`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const remove = async (h) => {
    if (!window.confirm(`Remove ${h.name} (${h.date})?`)) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const branches = Array.from(new Set(rows.map((r) => r.branch).filter(Boolean))).sort();
  const visible = rows.filter((r) => branchFilter === "all" || !r.branch || r.branch === branchFilter);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Calendar</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Holidays</h1>
          <p className="text-sm text-zinc-500 mt-2" data-testid="holiday-scope-note">
            {canManage ? "Company-wide and branch-specific holidays for the year." : myBranch ? <>Showing company-wide holidays plus those for your branch: <b>{myBranch}</b>.</> : "Showing company-wide holidays (no branch set on your profile)."}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2 items-center">
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} data-testid="holiday-file-input" />
            <Button variant="outline" onClick={downloadTemplate} className="rounded-sm" data-testid="holiday-template-btn"><DownloadSimple size={16} className="mr-1.5" />Template</Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-sm" data-testid="holiday-upload-btn"><UploadSimple size={16} className="mr-1.5" />{importing ? "Uploading…" : "Upload holiday list"}</Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="btn-zenith rounded-sm" data-testid="holiday-create-btn"><Plus size={16} className="mr-1.5" />Add holiday</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-display text-2xl">Add holiday</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm" data-testid="holiday-date-input" /></div>
                  <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="holiday-name-input" /></div>
                  <div className="space-y-1 col-span-2"><Label className="text-xs">Branch / location (optional — blank = all branches)</Label><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="e.g. Mumbai" className="rounded-sm" data-testid="holiday-branch-input" /></div>
                </div>
                {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submit} className="btn-zenith rounded-sm" data-testid="holiday-save-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {canManage && (
        <div className="text-xs text-zinc-500 -mt-2" data-testid="holiday-upload-hint">
          Upload a CSV/XLSX with columns <span className="font-mono-plex">date, name, branch, type</span>. Leave branch blank for all locations; separate several branches with “;”.
        </div>
      )}
      {branches.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center" data-testid="holiday-branch-filter">
          <MapPin size={14} className="text-zinc-400" />
          {["all", ...branches].map((b) => (
            <button key={b} onClick={() => setBranchFilter(b)} className={`text-xs px-2.5 py-1 border rounded-sm ${branchFilter === b ? "bg-adb-navy text-white border-adb-navy" : "border-zinc-200 hover:bg-zinc-50"}`} data-testid={`holiday-branch-${b}`}>{b === "all" ? "All branches" : b}</button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.length === 0 && <div className="col-span-full text-center py-12 border border-zinc-200 bg-white text-sm text-zinc-500 rounded-sm">No holidays configured.</div>}
        {visible.map((h) => {
          const d = new Date(h.date + "T00:00:00");
          const isPast = d < new Date(new Date().toDateString());
          return (
            <div key={h.id} className={`border border-zinc-200 rounded-sm p-4 flex items-center gap-4 bg-white ${isPast ? "opacity-60" : ""}`} data-testid={`holiday-card-${h.id}`}>
              <div className="w-14 h-14 border border-emerald-200 rounded-sm grid place-items-center bg-emerald-50 text-emerald-800">
                <div className="text-[10px] uppercase tracking-widest">{d.toLocaleDateString(undefined, { month: "short" })}</div>
                <div className="text-2xl font-display font-bold leading-none">{d.getDate()}</div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-zinc-500">{d.toLocaleDateString(undefined, { weekday: "long" })}</div>
                <div className="font-semibold text-zinc-900">{h.name}</div>
                <div className="flex gap-1 mt-1">
                  {h.branch && <Badge variant="outline" className="rounded-sm text-[10px]" data-testid={`holiday-branch-badge-${h.id}`}><MapPin size={10} className="mr-0.5" />{h.branch}</Badge>}
                  {h.type && h.type !== "public" && <Badge variant="outline" className="rounded-sm text-[10px] capitalize">{h.type}</Badge>}
                </div>
              </div>
              {canManage ? (
                <button onClick={() => remove(h)} className="text-zinc-400 hover:text-red-600" data-testid={`holiday-delete-${h.id}`}><Trash size={14} /></button>
              ) : <CalendarStar size={20} className="text-orange-500" weight="duotone" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
